import { createAuthClient } from 'better-auth/react';
import { organizationClient } from 'better-auth/client/plugins';
import type { BetterAuthClientPlugin } from 'better-auth/client';
import { apiClient, throwApiError } from 'api/apiClient';

// better-auth 1.6.x ships an `organizationClient()` plugin whose `$InferServerPlugin`
// type does not structurally satisfy the `BetterAuthClientPlugin` constraint, which
// collapses generic inference on `createAuthClient` and hides the `organization` API.
// Casting the plugin to the expected element type restores the inferred client API
// (including the precisely-typed `authClient.organization.*` methods) without `any`.
const organizationClientPlugin = organizationClient() as unknown as BetterAuthClientPlugin;

const baseURL = import.meta.env.VITE_SERVER_URL || window.location.origin;
const authBasePath = `${baseURL}/api/auth`;

// Shape of errors thrown by better-auth / the auth client wrappers. Errors may be
// plain thrown objects (see `throwResponseError`) or nested provider errors, so every
// field is optional and accessed defensively by callers.
export interface AuthClientError {
  message?: string;
  code?: string;
  status?: number;
  error?: {
    message?: string;
    code?: string;
    status?: number;
  };
  response?: {
    status?: number;
    data?: {
      message?: string;
      code?: string;
    };
  };
}

// Normalizes an unknown caught value into the optional `AuthClientError` shape so
// callers can read fields without `any`. No runtime coercion beyond a type view.
export function asAuthClientError(error: unknown): AuthClientError {
  return (error ?? {}) as AuthClientError;
}

async function throwResponseError(response: Response): Promise<never> {
  let errorBody: any = null;
  try {
    errorBody = await response.json();
  } catch {
    errorBody = null;
  }

  if (errorBody && typeof errorBody === 'object') {
    throw {
      ...errorBody,
      status: response.status,
    };
  }

  throw {
    message: typeof errorBody === 'string' && errorBody ? errorBody : response.statusText || 'Request failed',
    status: response.status,
  };
}

export const authClient = createAuthClient({
  baseURL,
  plugins: [organizationClientPlugin],
  fetchOptions: {
    credentials: 'include',
  },
  sessionOptions: {
    refetchOnWindowFocus: false,
  },
});

export async function listOrganizations() {
  return authClient.organization.list({
    fetchOptions: {
      throw: true,
    },
  });
}

export async function getActiveOrganization() {
  return authClient.organization.getFullOrganization({
    fetchOptions: {
      throw: true,
    },
  });
}

export async function setActiveOrganization(organizationId: any) {
  return authClient.organization.setActive({
    organizationId,
    fetchOptions: {
      throw: true,
    },
  });
}

export async function setAdminActiveOrganization(organizationId: string) {
  const response = await fetch(`${baseURL}/api/admin/session`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ activeOrganizationId: organizationId }),
  });

  if (!response.ok) await throwResponseError(response);
}

export async function listAdminOrganizations() {
  const response = await fetch(`${baseURL}/api/admin/organizations`, {
    credentials: 'include',
  });
  if (!response.ok) await throwResponseError(response);
  return response.json() as Promise<{ id: string; name: string }[]>;
}

export async function getActiveMemberRole() {
  return authClient.organization.getActiveMemberRole({
    fetchOptions: {
      throw: true,
    },
  });
}

type CreateOrganizationInput = {
  name: string;
  billingAddressStreet: string;
  billingAddressStreetNumber: string;
  billingAddressPostalCode: string;
  billingAddressCountry: string;
};

export async function createOrganization({
  name,
  billingAddressStreet,
  billingAddressStreetNumber,
  billingAddressPostalCode,
  billingAddressCountry,
}: CreateOrganizationInput) {
  const slugBase = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const slug = `${slugBase || 'organization'}-${crypto.randomUUID().slice(0, 8)}`;

  return authClient.organization.create({
    name,
    slug,
    billingAddressStreet,
    billingAddressStreetNumber,
    billingAddressPostalCode,
    billingAddressCountry,
    fetchOptions: {
      throw: true,
    },
  } as any);
}

export async function updateOrganization(
  data: {
    name?: string;
    slug?: string;
    logo?: string;
    metadata?: Record<string, any>;
    billingAddressStreet?: string;
    billingAddressStreetNumber?: string;
    billingAddressPostalCode?: string;
    billingAddressCountry?: string;
  },
  organizationId?: string
) {
  return authClient.organization.update({
    data,
    ...(organizationId ? { organizationId } : {}),
    fetchOptions: {
      throw: true,
    },
  });
}

export async function deleteOrganization(organizationId: string) {
  return authClient.organization.delete({
    organizationId,
    fetchOptions: {
      throw: true,
    },
  });
}

export async function leaveOrganization(organizationId: string) {
  return authClient.organization.leave({
    organizationId,
    fetchOptions: {
      throw: true,
    },
  });
}

export async function verifyCurrentUserPassword(password: string) {
  const response = await authClient.$fetch('/verify-password', {
    method: 'POST',
    body: {
      password,
    },
  });

  if (response.error) {
    throw {
      ...response.error,
      status: response.error.status,
    };
  }

  return response.data;
}

export async function inviteOrganizationMember(
  email: string,
  role: 'owner' | 'admin' | 'editor' | 'viewer',
  projects: string[] = [],
  resend = false
) {
  const response = await apiClient.api.invites.post({
    email,
    role,
    resend,
    projectIds: projects,
  });

  if (response.error) {
    throwApiError(response.error.value, response.error.status, true);
  }

  return response.data;
}

export async function listOrganizationMembers() {
  return authClient.organization.listMembers({
    fetchOptions: {
      throw: true,
    },
  });
}

export async function listOrganizationInvitations() {
  return authClient.organization.listInvitations({
    fetchOptions: {
      throw: true,
    },
  });
}

export async function listUserOrganizationInvitations() {
  return authClient.organization.listUserInvitations({
    fetchOptions: {
      throw: true,
    },
  });
}

export async function acceptOrganizationInvitation(invitationId: string) {
  return authClient.organization.acceptInvitation({
    invitationId,
    fetchOptions: {
      throw: true,
    },
  });
}

export async function updateOrganizationMemberRole(memberId: string, role: string, organizationId?: string) {
  return authClient.organization.updateMemberRole({
    memberId,
    role,
    ...(organizationId ? { organizationId } : {}),
    fetchOptions: {
      throw: true,
    },
  });
}

export async function removeOrganizationMember(memberIdOrEmail: string, organizationId?: string) {
  return authClient.organization.removeMember({
    memberIdOrEmail,
    ...(organizationId ? { organizationId } : {}),
    fetchOptions: {
      throw: true,
    },
  });
}

export async function cancelOrganizationInvitation(invitationId: string) {
  return authClient.organization.cancelInvitation({
    invitationId,
    fetchOptions: {
      throw: true,
    },
  });
}

export async function listActiveSubscriptions(referenceId?: string) {
  const url = new URL(`${authBasePath}/subscription/list`);

  if (referenceId) {
    url.searchParams.set('referenceId', referenceId);
  }

  const response = await fetch(url.toString(), {
    credentials: 'include',
  });

  if (!response.ok) {
    return throwResponseError(response);
  }

  return response.json();
}

export async function sendVerificationEmail(email: string, callbackURL = '/email-confirmed') {
  const resolvedCallbackURL =
    typeof callbackURL === 'string' && callbackURL.startsWith('/')
      ? `${window.location.origin}${callbackURL}`
      : callbackURL;

  return authClient.sendVerificationEmail({
    email,
    callbackURL: resolvedCallbackURL,
    fetchOptions: {
      throw: true,
    },
  });
}

export async function verifyEmail(token: string, callbackURL = '/login') {
  const url = new URL(`${authBasePath}/verify-email`);
  url.searchParams.set('token', token);
  if (callbackURL) {
    const resolvedCallbackURL =
      typeof callbackURL === 'string' && callbackURL.startsWith('/')
        ? `${window.location.origin}${callbackURL}`
        : callbackURL;
    url.searchParams.set('callbackURL', resolvedCallbackURL);
  }

  const response = await fetch(url.toString(), {
    credentials: 'include',
    redirect: 'manual',
  });

  if (response.type === 'opaqueredirect') {
    return null;
  }

  if (response.status >= 300 && response.status < 400) {
    return null;
  }

  if (!response.ok) {
    return throwResponseError(response);
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  return null;
}

export async function changePassword(currentPassword: string, newPassword: string) {
  return authClient.changePassword({
    currentPassword,
    newPassword,
    fetchOptions: {
      throw: true,
    },
  });
}
